import {
    Injectable,
    Injector,
    inject
} from "@angular/core";

import { TilbyCurrencyPipe } from "@tilby/tilby-ui-lib/pipes/tilby-currency";

import {
    digitalPaymentsManager,
    documentPrinter,
    fiscalUtils
} from "app/ajs-upgraded-providers";

import {
    ConfigurationManagerService,
    EntityManagerService,
    isCashdrawerMethod,
} from "src/app/core";

import {
    AlertDialogService,
    ConfirmDialogService,
    ReceiptOptionsDialogStateService
} from "src/app/dialogs";

import {
    ActiveSaleService,
    SaleBatchPrintService,
    SalePaymentService,
    SaleUtilsService,
} from "src/app/features/cashregister";

import {
    groupBy,
    keyBy,
    MathUtils
} from "src/app/shared/utils";

import {
    paymentMethodTypes,
    Sales,
    SalesCustomer,
    SalesPayments
} from "tilby-models";

type EmitSaleOptions = {
    runSaleChecks?: boolean;
    skipCheckPayments?: boolean;
    skipReceiptOptions?: boolean;
    hideResultNotes?: boolean;
    skipCashLimitCheck?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class ActiveSalePaymentService {
    private readonly injector = inject(Injector);
    private readonly activeSaleService = inject(ActiveSaleService);
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly digitalPaymentsManager = inject(digitalPaymentsManager);
    private readonly documentPrinterService = inject(documentPrinter);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly fiscalUtils = inject(fiscalUtils);
    private readonly receiptOptionsDialogService = inject(ReceiptOptionsDialogStateService);
    private readonly salePaymentService = inject(SalePaymentService);
    private readonly saleUtilsService = inject(SaleUtilsService);
    private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);

    private async checkPayments() {
        const sale: Sales = this.activeSaleService.currentSale;

        //Positive amount (sales)
        if ((sale.final_amount || 0) >= 0) {
            this.activeSaleService.cleanPaymentsFromSale();
            return;
        }

        //Negative amount (refunds/voids/credit notes/etc)
        if ((sale.payments?.length || 0) > 1 && sale.sale_parent_id) { //Multiple payments
            const parentSale = await this.entityManagerService.sales.fetchOneOfflineFirst(sale.sale_parent_id);

            if (MathUtils.round(parentSale?.final_amount!) !== -MathUtils.round(sale.final_amount!)) {
                throw 'CASHREGISTER.ACTIVE_SALE.REFUND_WITH_MULTIPLE_PAYMENTS_NOT_ALLOWED';
            }

            if (parentSale?.change) {
                let targetPayment;

                switch (parentSale.change_type) {
                    case 'cash':
                        targetPayment = sale.payments?.find((payment) => this.fiscalUtils.isCashPayment(payment.payment_method_type_id) && (Math.abs(payment.amount) > (parentSale.change || 0)));
                        break;
                    case 'ticket':
                        targetPayment = sale.payments?.find((payment) => (payment.payment_method_type_id === 6) && (Math.abs(payment.amount) > (parentSale.change || 0)));
                        break;
                }

                if (targetPayment) {
                    targetPayment.amount = MathUtils.round(targetPayment.amount + parentSale.change);
                }
            }
        } else { //Single/no payments
            const currentPayment = sale.payments?.[0];

            if (currentPayment) {
                currentPayment.amount = this.salePaymentService.getTotalAmountToPay(sale);
            }
        }

        let digitalPayment = sale.payments?.find((payment) => this.digitalPaymentsManager.isPaymentDigital(payment.payment_method_type_id) && this.digitalPaymentsManager.paymentAllowsRefund(payment.payment_method_type_id));

        if (digitalPayment) {
            let answer = await this.confirmDialogService.openDialog({
                data: {
                    messageLabel: 'CASHREGISTER.ACTIVE_SALE.REFUND_DIGITAL_PAYMENT_PROMPT',
                    messageParams:
                    {
                        amount: this.tilbyCurrencyPipe.transform(Math.abs(digitalPayment.amount)),
                        paymentMethod: digitalPayment.payment_method_name
                    },
                    confirmLabel: digitalPayment.payment_method_name,
                    cancelLabel: 'CASHREGISTER.ACTIVE_SALE_MODEL.CASH'
                }
            });

            if (!answer) {
                return this.activeSaleService.cashPayment();
            }
        }
    };

    public async beforePayment(options?: { skipCheckPayments?: boolean }) {
        await this.activeSaleService.verifyPrinterDocumentDataAndSelect();
        await this.activeSaleService.checkSaleConfirm();

        if (!options?.skipCheckPayments) {
            await this.checkPayments();
        }

        await this.documentPrinterService.checkSale(this.activeSaleService.currentSale, this.activeSaleService.printerDocumentData!);
    };

    private async showReceiptOptions() {
        if (!this.activeSaleService.printerDocumentData || !this.activeSaleService.currentSale) {
            return;
        }

        if (!this.configurationManagerService.getPreference('cashregister.enable_receipt_options')) {
            return;
        }

        const res = await this.receiptOptionsDialogService.openDialog({
            data: {
                printerDocumentData: this.activeSaleService.printerDocumentData,
                saleUuid: this.activeSaleService.currentSale.uuid!,
                saleCustomer: this.activeSaleService.currentSale.sale_customer
            }
        });

        if (!res) {
            throw 'CANCELED';
        }

        const customerData: Partial<SalesCustomer> = {};

        this.activeSaleService.printerDocumentData.options.courtesyReceipt = !!res.courtesyReceipt;
        this.activeSaleService.printerDocumentData.options.eReceipt = ['email', 'sms', 'save'].includes(res.mode);

        if (res.mode === 'email') {
            customerData.email = res.email;
        }

        if (Object.keys(customerData).length) {
            this.activeSaleService.updateSaleDetails({
                sale_customer: this.activeSaleService.currentSale.sale_customer ? {
                    ...this.activeSaleService.currentSale.sale_customer,
                    ...customerData
                } : {
                    ...customerData,
                    first_name: ' ',
                    last_name: ' '
                }
            });
        }
    }

    private allowedCategoriesOverridePayments = new Set([
        ...paymentMethodTypes.filter(pm => isCashdrawerMethod(pm.id)).map(pm => pm.id), //Cashdrawers
        42, //ZPayBridge
    ]);

    /**
     * Handles category-based split sales by checking for printer overrides
     * and splitting the sale items accordingly. If any categories have printer
     * overrides, it creates new sales for these items and reassigns the payments.
     * 
     * If there are no overrides, the function returns `false`.
     * 
     * Throws an error if there are multiple payments in the sale or if there are digital payments aside from the supported ones.
     * 
     * @returns {Promise<boolean>} true if the active sale has been handled, false otherwise
     */
    public async handleCategorySplitSales() {
        // Check if the sale has any categories with printer overrides
        const categoriesWithOverrides = await this.entityManagerService.categories.fetchCollectionOffline()
            .then(cs => cs.filter(c => c.printer_id))
            .then(cs => keyBy(cs, c => c.id));
        
        const saleItems = this.activeSaleService.currentSale.sale_items || [];
        const saleItemsWithOverrides = structuredClone(saleItems.filter(si => si.category_id && categoriesWithOverrides[si.category_id]));

        // Return to normal flow if there are no overrides
        if (!saleItemsWithOverrides.length) {
            return false;
        }

        const categoriesInSale = new Set(saleItems.map(si => si.category_id));
        const salePayments = this.activeSaleService.currentSale.payments || [];
        const paymentMethods = await this.entityManagerService.paymentMethods.fetchCollectionOffline().then(methods => keyBy(methods, m => m.id));

        if (salePayments.length! > 1 && categoriesInSale.size > 1) {
            throw 'CASHREGISTER.ACTIVE_SALE.CATEGORIES_OVERRIDE.MULTIPLE_PAYMENTS_NOT_ALLOWED';
        }

        if (categoriesInSale.size > 1 && this.digitalPaymentsManager.isPaymentDigital(salePayments[0].payment_method_type_id) && !this.allowedCategoriesOverridePayments.has(salePayments[0].payment_method_type_id)) {
            throw 'CASHREGISTER.ACTIVE_SALE.CATEGORIES_OVERRIDE.DIGITAL_PAYMENT_NOT_ALLOWED';
        }

        let targetPayment = salePayments[0];

        const templatePayment: SalesPayments = {
            payment_method_id: targetPayment.payment_method_id,
            payment_method_type_id: targetPayment.payment_method_type_id,
            payment_method_name: targetPayment.payment_method_name,
            payment_method_type_name: targetPayment.payment_method_type_name,
            amount: 0
        };

        let idx = 1;
        let currentSaleHandledOnPrinter: string | undefined = undefined;

        const splitSales: { sale: Sales, printerId: string }[] = [];
        let splitSalesAmount = 0;

        const itemsByPrinter = groupBy(saleItemsWithOverrides, si => categoriesWithOverrides[si.category_id!]?.printer_id);

        for (const [printerId, saleItems] of Object.entries(itemsByPrinter)) {
            // If this printer handles the whole sale, override the printer and, after processing, inform the caller that the main sale has been processed
            if (this.activeSaleService.currentSale.sale_items?.length! === saleItems.length) {
                currentSaleHandledOnPrinter = printerId;
                continue;
            }

            const itemsToAdd = saleItems
                .flatMap(saleItem => this.activeSaleService.removeSaleItem(saleItem)) //Remove items from active sale
                .map(si => this.saleUtilsService.getCleanSaleItem(si)); // Cleanup the sale item

            const sale = await this.activeSaleService.createNewSale(undefined, {
                dontOpenSale: true,
                overrides: {
                    name: `${this.activeSaleService.currentSale.name} - ${idx++}`.slice(0, 30),
                    sale_items: itemsToAdd,
                }
            });

            const amount = this.activeSaleService.getFastPaymentAmount(sale, paymentMethods[String(templatePayment.payment_method_id)], sale.final_amount);

            sale.payments = [{
                ...templatePayment,
                amount: amount,
            }];

            splitSales.push({
                sale,
                printerId
            });

            splitSalesAmount = MathUtils.round(splitSalesAmount + amount);
        }

        const remainingAmount = this.activeSaleService.getFastPaymentAmount(this.activeSaleService.currentSale, paymentMethods[salePayments[0].payment_method_id]);

        splitSalesAmount = MathUtils.round(splitSalesAmount + remainingAmount);

        if(currentSaleHandledOnPrinter) {
            splitSales.push({
                sale: structuredClone(this.activeSaleService.currentSale),
                printerId: currentSaleHandledOnPrinter
            });
        }

        if(isCashdrawerMethod(salePayments[0].payment_method_type_id)) {
            //TODO: since we are processing the payments here, we also should handle the resulting tips. For now it's fine since we don't have tip management in cashdrawers
            const paymentResult = await this.digitalPaymentsManager.digitalPayment(splitSalesAmount, salePayments[0].payment_method_id, { sale: structuredClone(this.activeSaleService.currentSale) });

            targetPayment = Array.isArray(paymentResult) ? paymentResult[0] : paymentResult;
            targetPayment.paid = true;

            for(const { sale } of splitSales) {
                if(sale.payments?.[0]) {
                    sale.payments[0].paid = true;
                }
            }
        }

        //Update payment info on the main sale
        this.activeSaleService.updateSaleDetails({
            payments: [{
                ...targetPayment,
                amount: remainingAmount
            }]
        });

        const saleBatchPrintService = this.injector.get(SaleBatchPrintService);

        // Print the split sales
        for (const { sale, printerId } of splitSales) {
            const printer = await this.entityManagerService.printers.fetchOneOffline(parseInt(printerId));

            let documentType = this.activeSaleService.printerDocumentData?.document_type.id;

            if(documentType === 'generic_receipt' && ['fiscal', 'rt'].includes(printer?.type!)) {
                documentType = 'fiscal_receipt';
            } else if (documentType === 'fiscal_receipt' && printer?.type === 'receipt') {
                documentType = 'generic_receipt';
            }

            await saleBatchPrintService.printSale(sale, parseInt(printerId), documentType as any, { skipSaleCheck: true, useEReceipt: !!this.activeSaleService.printerDocumentData?.options.eReceipt });
        }

        return !!currentSaleHandledOnPrinter;
    }

    public async emitSale(options?: EmitSaleOptions) {
        if (this.activeSaleService.paymentInProgress()) {
            return;
        }

        const currentPayments = JSON.stringify(this.activeSaleService.currentSale?.payments || []);
        this.activeSaleService.paymentInProgress.set(true);

        try {
            if(!options?.skipReceiptOptions) {
                //Show receipt options if enabled
                try {
                    await this.showReceiptOptions();
                } catch (e) {
                    throw 'CANCELED';
                }
            }

            const handled = await this.handleCategorySplitSales();

            if (handled) {
                //The whole sale has been emitted through the split printing flow, so there is no emitted document to return
                return { splitHandled: true };
            }

            if (options?.runSaleChecks) {
                await this.beforePayment({ skipCheckPayments: options?.skipCheckPayments });
            }

            await this.activeSaleService.processPayments();
            await this.activeSaleService.processSaleChange();

            return await this.emitDocument(options);
        } catch(err) {        
            if (currentPayments !== JSON.stringify(this.activeSaleService.currentSale?.payments || [])) {
                this.activeSaleService.updateSaleDetails({
                    payments: this.activeSaleService.currentSale?.payments
                });

                this.activeSaleService.saveSale();
            }

            throw err;
        } finally {
            this.activeSaleService.paymentInProgress.set(false);
        }
    }

    private async emitDocument(options?: EmitSaleOptions) {
        try {
            // If the cash limit is enabled, check if the sum of cash payments is greater than 5000 (only for Italy)
            if (!options?.skipCashLimitCheck && this.configurationManagerService.getShopCountry() === 'IT' && !this.configurationManagerService.getPreference('cashregister.disable_cash_limit_exceeded_confirm')) {
                const cashSum = ((this.activeSaleService.currentSale?.payments || []) as SalesPayments[])
                    .reduce((acc, payment) => this.fiscalUtils.isCashPayment(payment.payment_method_type_id) ? acc + payment.amount : acc, 0);

                if (cashSum >= 5000) {
                    const result = await this.confirmDialogService.openDialog({
                        data: {
                            messageLabel: 'CASHREGISTER.ACTIVE_SALE.CASH_LIMIT_EXCEEDED_CONFIRM'
                        }
                    });

                    if (!result) {
                        throw { printError: 'CANCELED' };
                    }
                }
            }

            const result = await this.activeSaleService.emitDocument();

            if ('printError' in result) {
                throw result;
            } else if (!options?.hideResultNotes && 'notes' in result && result.notes?.length) {
                //TODO: Support multiple warning messages
                if (result.notes[0] === "CHECK_PRINTER_CLOCK") {
                    this.alertDialogService.openDialog({ data: { messageLabel: 'CASHREGISTER.ACTIVE_SALE.TIME_NOT_ALIGNED' } });
                }
            }

            return result;
        } catch (error: any) {
            if (error?.printError) {
                throw error.printError;
            }
        }
    }
}